import React, { useEffect, useState, useCallback } from "react";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Spinner } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CommonDataTable from "../../common/DataTable";
import { getUserColumns } from "../../Components/Common/columns/Users";
import {fetchAllUsers, deleteUserById, updateUserById, updateUserStatusById, createUser, bulkDeleteUsers} from "../../slices/thunks";
import { showToast } from "../../slices/toast/reducer";
import DeleteDailog from "../../Components/Common/DeleteDailog";
import SubscriptionTransactionsDialog from "./SubscriptionTransactionsDialog";

const Users = () => {
  const dispatch = useDispatch();

  const [filterParams, setFilterParams] = useState({
    page: 1,
    perPage: 10,
    limit: 10,
    search: "",
    orderBy: "createdAt",
    order: "desc",
    statusFilter: "all",
  });

  const { users, totalRows, loading } = useSelector((state) => ({
    users: state.Users?.users || [],
    totalRows: state.Users?.totalRows || 0,
    loading: state.Users?.loading || false,
  }));

  const initialFormState = {
    _id: "",
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    isAdmin: false,
    status: "active"
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [pendingStatusMap, setPendingStatusMap] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchData = useCallback(async (params = {}) => {
    const updated = {
      ...filterParams,
      ...params,
      limit: params.perPage || filterParams.limit,
      page: params.page || params.currentPage || filterParams.page,
      statusFilter: params.statusFilter ?? filterParams.statusFilter,
    };

    setFilterParams(updated);
    return await dispatch(fetchAllUsers(updated));
  }, [dispatch, filterParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleRowSelected = (rows) => {
    setSelectedRows(rows);
  };

  const openConfirm = (message, action) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmPopup(true);
  };

  const handleDeleteUser = async (event, id) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const result = await dispatch(deleteUserById(id));

      if (result?.ok) {
        const postsNote =
          typeof result?.deletedPosts === "number" && result.deletedPosts >= 0
            ? ` Removed ${result.deletedPosts} related posts.`
            : "";

        dispatch(showToast({
          type: "success",
          msg: (result?.message || "User deleted successfully") + postsNote
        }));

        await fetchData();

      } else {
        dispatch(
          showToast({
            type: "error",
            msg: result?.message || "Failed to delete user",
          })
        );
      }
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          msg: error.message || "Failed to delete user",
        })
      );
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      dispatch(
        showToast({
          type: "warning",
          msg: "Please select at least one user to delete",
        })
      );
      return;
    }

    const userIds = selectedRows.map((row) => row._id || row.id);

    openConfirm(
      `Are you sure you want to delete ${userIds.length} users?`,
      async () => {
        setIsBulkDeleting(true);
        try {
          const result = await dispatch(bulkDeleteUsers(userIds));

          if (result?.ok) {
            const postsNote =
              typeof result?.deletedPosts === "number" && result.deletedPosts >= 0
                ? ` Removed ${result.deletedPosts} related posts.`
                : "";
            dispatch(
              showToast({
                type: "success",
                msg: (result.message || `Successfully deleted ${userIds.length} users`) + postsNote,
              })
            );

            const statusUpdates = {};
            setPendingStatusMap(prev => ({ ...prev, ...statusUpdates }));

            setSelectedRows([]);

            await fetchData();

          } else {
            dispatch(
              showToast({
                type: "error",
                msg: result?.message || "Failed to delete users",
              })
            );
          }
        } catch (error) {
          dispatch(
            showToast({
              type: "error",
              msg: error.message || "Failed to delete users",
            })
          );
        } finally {
          setIsBulkDeleting(false);
          setShowConfirmPopup(false); 
        }
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setModalLoading(true);

    const data = {
      firstname: form.firstname,
      lastname: form.lastname,
      email: form.email,
      password: form.password,
      isAdmin: form.isAdmin,
      ...(isEdit && { status: form.status })
    };

    let result;

    try {
      if (isEdit) {
        result = await dispatch(updateUserById(form._id, data));
      } else {
        result = await dispatch(createUser({ ...data, status: "active" }));
      }

      if (result?.ok) {
        dispatch(
          showToast({
            type: "success",
            msg: result?.message || "User saved successfully!",
          })
        );

        setModalOpen(false);
        setForm(initialFormState);

      } else {
        dispatch(
          showToast({
            type: "error",
            msg: result?.message || "Something went wrong!",
          })
        );
      }
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          msg: error.message || "Something went wrong!",
        })
      );
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditOpen = (row) => {
    setIsEdit(true);
    setForm({
      _id: row._id || row.id,
      firstname: row.firstname || "",
      lastname: row.lastname || "",
      email: row.email || "",
      password: "",
      isAdmin: !!row.isAdmin,
      status: row.status || "active"
    });
    setModalOpen(true);
  };

  const handleAddOpen = () => {
    setIsEdit(false);
    setForm(initialFormState);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalLoading(false);
    setForm(initialFormState);
  };

  const handleStatusChange = async (row, status) => {
    const id = row._id || row.id;

    if (pendingStatusMap[id]) return;

    try {
      setPendingStatusMap((prev) => ({ ...prev, [id]: status }));
      const result = await dispatch(updateUserStatusById(id, status));

      if (result?.ok) {
        dispatch(showToast({
          type: "success",
          msg: `User status changed to ${status}`,
        }));

      } else {
        dispatch(showToast({
          type: "error",
          msg: result?.message || "Failed to update status",
        }));
      }
    } catch (error) {
      dispatch(showToast({
        type: "error",
        msg: error.message || "Failed to update status",
      }));
    } finally {
      setPendingStatusMap((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const getFilteredUsers = useCallback(() => {
    let filteredUsers = users.map((u) => ({
      ...u,
      id: u._id || u.id,
      createdAt: u.createdAt || u.created_at,
      status: pendingStatusMap[u._id || u.id] || u.status || "active",
    }));

    if (filterParams.statusFilter !== "all") {
      filteredUsers = filteredUsers.filter(user => {
        if (filterParams.statusFilter === "active") return user.status === "active" && !user.isDeleted;
        if (filterParams.statusFilter === "inactive") return user.status === "inactive" && !user.isDeleted;
        if (filterParams.statusFilter === "deleted") return user.isDeleted === true;
        return true;
      });
    }

    return filteredUsers;
  }, [users, pendingStatusMap, filterParams.statusFilter]);

  const processedUsers = getFilteredUsers();

  const renderStatusFilter = (
    <div className="d-flex flex-column">
      <Input
        id="user-status-filter"
        type="select"
        style={{
          width: 'fit-content'
        }}
        value={filterParams.statusFilter}
        onChange={(e) =>
          fetchData({
            statusFilter: e.target.value,
            page: 1,
            currentPage: 1,
          })
        }
      >
        <option value="all">All</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
        <option value="deleted">Deleted</option>
      </Input>
    </div>
  );

  const handleViewSubscriptions = (user) => {
    setSelectedUser(user);
    setSubscriptionDialogOpen(true);
  };

  const columns = getUserColumns({
    onDeleteClick: handleDeleteUser,
    onEditClick: handleEditOpen,
    onStatusChange: handleStatusChange,
    onViewSubscriptions: handleViewSubscriptions,
  });

  document.title = "Users | " + process.env.REACT_APP_SITE_TITLE;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Users" pageTitle="View Users" />

          <CommonDataTable
            columns={columns}
            data={processedUsers}
            totalRows={totalRows}
            loading={loading}
            fetchData={fetchData}
            filterParams={filterParams}
            selectableRowDisabled={(row) => row.isDeleted}
            checkboxEnabled={true}
            searchEnable={true}
            showExportButton={true}
            showAddButton={true}
            addOnClickMethod={handleAddOpen}
            buttonName="Add New User"
            exportFileName="users"
            bulkDelete={handleBulkDelete}
            selectedRows={selectedRows}
            bulkSelected={handleRowSelected}
            isBulkDeleting={isBulkDeleting}
            customFilters={renderStatusFilter}
          />

          <Modal isOpen={modalOpen} toggle={handleModalClose} className="modal-fixed-header-footer">
            <ModalHeader toggle={!modalLoading ? handleModalClose : undefined}>
              {isEdit ? "Edit User" : "Add New User"}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
              <ModalBody>
                <FormGroup>
                  <Label for="firstname">First name</Label>
                  <Input
                    id="firstname"
                    value={form.firstname}
                    onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                    required
                    disabled={modalLoading}
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="lastname">Last name</Label>
                  <Input
                    id="lastname"
                    value={form.lastname}
                    onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                    required
                    disabled={modalLoading}
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    disabled={modalLoading}
                  />
                </FormGroup>

                {!isEdit && (
                  <FormGroup>
                    <Label for="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={form.password}
                      onChange={(e) =>
                        setForm({ ...form, password: e.target.value })
                      }
                      required={!isEdit}
                      disabled={modalLoading}
                    />
                  </FormGroup>
                )}

                <FormGroup check>
                  <Input
                    id="isAdmin"
                    type="checkbox"
                    checked={form.isAdmin}
                    onChange={(e) =>
                      setForm({ ...form, isAdmin: e.target.checked })
                    }
                    disabled={modalLoading}
                  />
                  <Label for="isAdmin" check>
                    Is Admin
                  </Label>
                </FormGroup>
              </ModalBody>

              <ModalFooter>
                <Button color="secondary" onClick={handleModalClose} disabled={modalLoading}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={modalLoading}>
                  {modalLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isEdit ? "Updating..." : "Saving..."}
                    </>
                  ) : (
                    isEdit ? "Update" : "Save"
                  )}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          <SubscriptionTransactionsDialog
            open={subscriptionDialogOpen}
            onClose={() => setSubscriptionDialogOpen(false)}
            user={selectedUser}
          />

          {showConfirmPopup && confirmAction && (
            <DeleteDailog
              isOpen={showConfirmPopup}
              title="Confirm Bulk Deletion"
              message={confirmMessage}
              confirmLabel={isBulkDeleting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting...
                </>
              ) : "Yes, Delete All"}
              cancelLabel="Cancel"
              confirmColor="danger"
              icon={isBulkDeleting ? "loading" : "warning"}
              onConfirm={() => {
                confirmAction();
              }}
              onCancel={() => {
                if (!isBulkDeleting) {
                  setShowConfirmPopup(false);
                }
              }}
              loading={isBulkDeleting}
              disabled={isBulkDeleting}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Users;